import { Component, OnInit, OnDestroy, DestroyRef, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { LoadingStatusComponent } from '../loading-status/loading-status.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, LoadingStatusComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  isLoggingIn = false;
  isLoadingUserInfo = false;
  private readonly LOADING_KEY = 'apex.login.loading';

  constructor(
    public authService: AuthService, 
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    // Remove no-scroll class when component is destroyed
    document.body.classList.remove('no-scroll');
  }

  ngOnInit(): void {
    // Prevent body scrolling when login component is active
    document.body.classList.add('no-scroll');
    
    // FIRST: Check sessionStorage for persisted loading state (most reliable)
    // This persists across Azure redirects and page reloads
    const hasPersistedLoading = sessionStorage.getItem(this.LOADING_KEY) === 'true';
    
    // SECOND: Check if we're returning from Azure redirect (URL has hash or code parameter)
    const isAzureRedirect = !!(window.location.hash || 
                               window.location.search.includes('code=') || 
                               window.location.search.includes('state=') ||
                               window.location.search.includes('error=') ||
                               sessionStorage.getItem('msal.login.started') !== null);
    
    // THIRD: Check if authenticated but user info not loaded yet (means we're loading)
    const isAuthenticatedButNoUser = this.authService.isAuthenticated() && !this.authService.getUser();
    
    // Show loading if ANY of these conditions are true
    if (hasPersistedLoading || isAzureRedirect || isAuthenticatedButNoUser) {
      // Immediately show loading status
      this.isLoadingUserInfo = true;
      // Ensure loading state is persisted
      sessionStorage.setItem(this.LOADING_KEY, 'true');
      // Force view update
      this.cdr.detectChanges();
    }

    // Subscribe to loading state - show loading page when user info is being loaded
    this.authService.isLoadingUserInfo$.subscribe(isLoading => {
      if (isLoading) {
        this.isLoadingUserInfo = true;
        sessionStorage.setItem(this.LOADING_KEY, 'true');
      } else {
        // Only clear loading state if user info is actually loaded
        if (this.authService.getUser()) {
          this.isLoadingUserInfo = false;
          sessionStorage.removeItem(this.LOADING_KEY);
        }
      }
    });

    // Subscribe to authentication state - if authenticated but no user, show loading
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth && !this.authService.getUser()) {
        this.isLoadingUserInfo = true;
        sessionStorage.setItem(this.LOADING_KEY, 'true');
      }
    });

    // Subscribe to user changes (this fires when user info is loaded)
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      if (user) {
        // User info is loaded - clear loading state
        this.isLoadingUserInfo = false;
        sessionStorage.removeItem(this.LOADING_KEY);
        // Check access and redirect
        this.checkAccessAndRedirect(user);
      }
    });

    // Also check immediately if already authenticated AND has user info
    if (this.authService.isAuthenticated()) {
      const user = this.authService.getUser();
      if (user) {
        // User already loaded, clear loading and redirect immediately
        this.isLoadingUserInfo = false;
        sessionStorage.removeItem(this.LOADING_KEY);
        this.checkAccessAndRedirect(user);
      } else {
        // Authenticated but no user info - show loading
        this.isLoadingUserInfo = true;
        sessionStorage.setItem(this.LOADING_KEY, 'true');
      }
    }
  }

  shouldShowLoading(): boolean {
    // Show loading if:
    // 1. User clicked login button (isLoggingIn is true)
    // 2. Explicitly set to loading
    // 3. Authenticated but user info not loaded yet
    // 4. URL indicates Azure redirect
    // 5. Loading state persisted in sessionStorage (survives page reload)
    const isAzureRedirect = !!(window.location.hash || 
                               window.location.search.includes('code=') || 
                               window.location.search.includes('state=') ||
                               window.location.search.includes('error=') ||
                               sessionStorage.getItem(this.LOADING_KEY) === 'true');
    
    return this.isLoggingIn ||
           this.isLoadingUserInfo || 
           (this.authService.isAuthenticated() && !this.authService.getUser()) ||
           isAzureRedirect;
  }

  shouldShowLoginForm(): boolean {
    // Only show login form if:
    // 1. Not currently logging in
    // 2. Not loading
    // 3. Not authenticated
    return !this.isLoggingIn && 
           !this.shouldShowLoading() && 
           !this.authService.isAuthenticated();
  }

  private checkAccessAndRedirect(user: any): void {
    // Remove no-scroll class before redirecting
    document.body.classList.remove('no-scroll');
    
    // Check if user has any permitted role (permission matrix)
    if (user.isAdmin || user.isPM) {
      this.router.navigate(['/dashboard/portfolio-overview']);
    } else if (user.isFinance) {
      this.router.navigate(['/allocations']);
    } else if (user.isPc) {
      this.router.navigate(['/employees']);
    } else if (user.isSoftware || user.isAccounting) {
      this.router.navigate(['/timesheet']);
    } else {
      this.router.navigate(['/unauthorized']);
    }
  }

  async login(): Promise<void> {
    if (this.isLoggingIn) {
      return; // Prevent multiple clicks
    }

    // Set loading state immediately so loading-status is shown
    this.isLoggingIn = true;
    this.isLoadingUserInfo = true;
    sessionStorage.setItem(this.LOADING_KEY, 'true');
    this.cdr.detectChanges();

    // Defer redirect so the browser can paint the loading screen before leaving
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

    try {
      await this.authService.login();
    } catch (error) {
      this.isLoggingIn = false;
      this.isLoadingUserInfo = false;
      sessionStorage.removeItem(this.LOADING_KEY);
      this.cdr.detectChanges();
    }
  }
}

